import { useMemo, useState } from 'react';
import { useParams } from 'react-router-dom';

import { useStream, useTranscript } from 'src/api/queries';
import { AbsenceState } from 'src/components/editorial/AbsenceState';
import { LoadFailure } from 'src/components/editorial/LoadFailure';
import { Icon } from 'src/components/ui/Icon';
import { Skeleton } from 'src/components/ui/Skeleton';
import { formatClock } from 'src/lib/time';
import type { EditorialTranscriptTurn } from 'src/types/editorial';

/** A fresh `?? []` on every render defeats the filter memo — this one is referentially stable. */
const NO_TURNS: EditorialTranscriptTurn[] = [];
/** Turns with an empty speaker render under this name, so the filter matches what the list shows. */
const UNKNOWN_SPEAKER = 'Hablante desconocido';

/** Answers: what was actually said, and when? Pure testimony — the whole screen is reading face. */
export function TranscriptWorkspace() {
  const { streamId = '' } = useParams<{ streamId: string }>();
  const { data, isPending, isError, error, refetch } = useTranscript(streamId);
  // Already warmed by StreamLayout under the same query key — this is a cache read, not a request.
  const { data: streamData } = useStream(streamId);
  const sourceUrl = streamData?.stream?.sourceUrl;
  const [query, setQuery] = useState('');
  const [voice, setVoice] = useState('');

  const turns = data?.transcript?.payload.turns ?? NO_TURNS;
  const speakers = useMemo(
    () => [...new Set(turns.map(turn => turn.speaker || UNKNOWN_SPEAKER))].sort((a, b) => a.localeCompare(b)),
    [turns]
  );
  const filtered = useMemo(() => {
    const needle = query.trim().toLowerCase();
    return turns.filter(turn => {
      if (voice && (turn.speaker || UNKNOWN_SPEAKER) !== voice) return false;
      if (!needle) return true;
      return turn.text.toLowerCase().includes(needle) || turn.speaker.toLowerCase().includes(needle);
    });
  }, [turns, query, voice]);

  if (isPending) return <div className="page-document"><Skeleton variant="document" count={8} /></div>;

  if (isError) {
    return (
      <div className="page-document">
        <LoadFailure title="No se pudo cargar el guion" error={error} onRetry={refetch} />
      </div>
    );
  }

  if (!data?.transcript) {
    return (
      <div className="page-document">
        <AbsenceState
          kind="not_reached"
          what="No hay guion para esta transmisión."
          why="La transcripción se guarda al cerrar el stream; una transmisión en vivo aún no la tiene completa."
        />
      </div>
    );
  }

  const filtering = query.trim() !== '' || voice !== '';
  const clearFilters = () => {
    setQuery('');
    setVoice('');
  };

  return (
    <div className="page-document">
      <div className="tx-head">
        <h2>Guion de la transmisión</h2>
        <span className="tx-count">
          {filtering ? `${filtered.length} de ${turns.length} turnos` : `${turns.length} turnos`}
        </span>
      </div>

      <section className="tx-filters" aria-label="Filtros del guion">
        <div className="tx-field tx-search">
          <label htmlFor="tx-q">Buscar</label>
          <div className="tx-search-box">
            <span className="tx-search-icon" aria-hidden="true">
              <Icon name="search" />
            </span>
            <input
              id="tx-q"
              type="search"
              value={query}
              onChange={event => setQuery(event.target.value)}
              placeholder="Buscar en el guion"
              autoComplete="off"
            />
          </div>
        </div>
        <div className="tx-field tx-field--select">
          <label htmlFor="tx-voice">Voz</label>
          <select id="tx-voice" value={voice} onChange={event => setVoice(event.target.value)}>
            <option value="">Todas</option>
            {speakers.map(speaker => (
              <option key={speaker} value={speaker}>
                {speaker}
              </option>
            ))}
          </select>
        </div>
        <button className="tx-clear" type="button" onClick={clearFilters} disabled={!filtering}>
          Limpiar
        </button>
      </section>

      <div className="guion">
        {filtered.length === 0 ? (
          <p className="guion-empty">
            {query.trim() ? `Ningún turno contiene «${query}».` : `Ningún turno de ${voice || 'esta voz'}.`}
          </p>
        ) : (
          <ol>
            {filtered.map((turn, index) => (
              <li key={`${turn.start_seconds}-${index}`} className="turn" id={`t-${turn.start_seconds}`}>
                {sourceUrl ? (
                  <a
                    className="turn-time"
                    href={`${sourceUrl}&t=${Math.floor(turn.start_seconds)}s`}
                    target="_blank"
                    rel="noreferrer noopener"
                    title="Ver en YouTube desde este momento"
                    aria-label={`Ver video desde ${formatClock(turn.start_seconds)} en YouTube`}
                  >
                    <Icon name="play" />
                    {formatClock(turn.start_seconds)}
                  </a>
                ) : (
                  <span className="turn-time">{formatClock(turn.start_seconds)}</span>
                )}
                <p className="turn-speaker">{turn.speaker || UNKNOWN_SPEAKER}</p>
                <p className="turn-text">{turn.text}</p>
              </li>
            ))}
          </ol>
        )}
      </div>
    </div>
  );
}
